import React from "react";
import { useQuery } from "@tanstack/react-query";
import { PageContainer } from "../../components/layout/PageContainer";
import { Button } from "../../ui/Button";
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/Card";
import { toast } from "../../ui/Toast";
import { ToastDemo } from "../../ui/ToastDemo";
import {
  executeBulkQueue,
  failBulkQueue,
  listBulkQueue,
  type BulkQueueItem,
} from "../../api/bulk-queue";


function formatIdr(value: number): string {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function SandboxPage() {
  const [queue, setQueue] = React.useState<BulkQueueItem[]>([]);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["bulk-queue"],
    queryFn: () => listBulkQueue({ limit: 50 }),
    staleTime: 15000,
  });

  React.useEffect(() => {
    if (data?.items) {
      setQueue(data.items);
    }
  }, [data]);

  const handleAction = (seqId: number, action: "execute" | "fail") => {
    const actionPromise =
      action === "execute" ? executeBulkQueue(seqId) : failBulkQueue(seqId);

    actionPromise
      .then(() => {
        setQueue((prev) => prev.filter((item) => item.seqId !== seqId));
        if (action === "execute") {
          toast.success("Bulk transaction executed.", { title: "Executed" });
        } else {
          toast.danger("Bulk transaction failed.", { title: "Failed" });
        }
      })
      .catch(() => {
        toast.danger("Action failed.", { title: "Bulk queue" });
      })
      .finally(() => {
        void refetch();
      });
  };

  return (
    <PageContainer
      title="Bulk Transactions Queue"
      description="Approve or fail pending bulk transactions from clients."
      width="full"
    >
      <Card className="p-0 overflow-hidden">
        <CardHeader className="px-4 py-3">
          <CardTitle as="h2">Pending queue</CardTitle>
          <CardDescription>
            Execute a transaction to process it, or fail it to reject the
            batch.
          </CardDescription>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[color:var(--kkp-surface-2)]">
                <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.75)]">
                  <th className="px-4 py-3">Seq ID</th>
                  <th className="px-4 py-3">Created At</th>
                  <th className="px-4 py-3">Bulk Tx ID</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3 text-right">Total Recipient</th>
                  <th className="px-4 py-3 text-right">Total Volume (IDR)</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-6 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      Loading queue…
                    </td>
                  </tr>
                ) : queue.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-6 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      No pending bulk transactions.
                    </td>
                  </tr>
                ) : (
                  queue.map((item) => (
                    <tr
                      key={item.seqId}
                      className="border-t border-[color:rgba(255,255,255,0.08)]"
                    >
                      <td className="px-4 py-3 font-semibold text-white">
                        {item.seqId}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {formatDateTime(item.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-white">
                        {item.bulkTxId}
                      </td>
                      <td className="px-4 py-3 text-white">{item.company}</td>
                      <td className="px-4 py-3 text-right text-white">
                        {item.totalRecipient.toLocaleString("id-ID")}
                      </td>
                      <td className="px-4 py-3 text-right text-white">
                        {formatIdr(item.totalVolume)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => handleAction(item.seqId, "execute")}
                          >
                            Execute
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleAction(item.seqId, "fail")}
                          >
                            Fail
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </PageContainer>
  );
}

type TabKey =
  | "overview"
  | "feedback"
  | "forms"
  | "data"
  | "navigation"
  | "content";

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold transition",
        active
          ? "bg-[color:rgba(255,255,255,0.08)] text-white"
          : "bg-transparent text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
      ].join(" ")}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
      {children}
    </div>
  );
}

type ToastVariant = "info" | "success" | "warning" | "danger";

function Divider({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-[color:rgba(255,255,255,0.10)]" />
      {label ? (
        <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
          {label}
        </div>
      ) : null}
      <div className="h-px flex-1 bg-[color:rgba(255,255,255,0.10)]" />
    </div>
  );
}

function KkpInput({
  id,
  label,
  hint,
  error,
  left,
  right,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  left?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1" htmlFor={id}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-[color:rgba(255,255,255,0.92)]">
          {label}
        </span>
        {right ? (
          <span className="text-[11px] text-[color:var(--kkp-text-muted)]">
            {right}
          </span>
        ) : null}
      </div>

      <div className="flex items-center gap-2 rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] px-3 py-2">
        {left ? <span className="shrink-0">{left}</span> : null}
        <div className="min-w-0 flex-1">{children}</div>
      </div>

      {error ? (
        <div className="text-xs text-[color:rgba(239,68,68,0.92)]">{error}</div>
      ) : hint ? (
        <div className="text-xs text-[color:var(--kkp-text-muted)]">{hint}</div>
      ) : null}
    </label>
  );
}

function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] px-3 py-2">
      <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm font-bold text-white">
        {value}
      </div>
      {note ? (
        <div className="mt-0.5 text-[11px] text-[color:rgba(255,255,255,0.65)]">
          {note}
        </div>
      ) : null}
    </div>
  );
}

function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "success" | "warning" | "danger" | "info";
  children: React.ReactNode;
}) {
  const c =
    tone === "success"
      ? "border-[color:rgba(34,197,94,0.55)] bg-[color:rgba(34,197,94,0.10)] text-[color:rgba(255,255,255,0.92)]"
      : tone === "warning"
        ? "border-[color:rgba(245,158,11,0.55)] bg-[color:rgba(245,158,11,0.10)] text-[color:rgba(255,255,255,0.92)]"
        : tone === "danger"
          ? "border-[color:rgba(239,68,68,0.55)] bg-[color:rgba(239,68,68,0.10)] text-[color:rgba(255,255,255,0.92)]"
          : tone === "info"
            ? "border-[color:rgba(203,72,226,0.55)] bg-[color:rgba(203,72,226,0.10)] text-[color:rgba(255,255,255,0.92)]"
            : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.03)] text-[color:rgba(255,255,255,0.88)]";

  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold",
        c,
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function variantLabel(v: ToastVariant): string {
  switch (v) {
    case "success":
      return "Success";
    case "warning":
      return "Warning";
    case "danger":
      return "Danger";
    case "info":
    default:
      return "Info";
  }
}

function Swatch({
  variant,
  active,
}: {
  variant: ToastVariant;
  active: boolean;
}) {
  const ring =
    variant === "success"
      ? "ring-[color:rgba(34,197,94,0.35)]"
      : variant === "warning"
        ? "ring-[color:rgba(245,158,11,0.35)]"
        : variant === "danger"
          ? "ring-[color:rgba(239,68,68,0.35)]"
          : "ring-[color:rgba(203,72,226,0.35)]";

  const fill =
    variant === "success"
      ? "bg-[color:rgba(34,197,94,0.18)] border-[color:rgba(34,197,94,0.55)]"
      : variant === "warning"
        ? "bg-[color:rgba(245,158,11,0.18)] border-[color:rgba(245,158,11,0.55)]"
        : variant === "danger"
          ? "bg-[color:rgba(239,68,68,0.18)] border-[color:rgba(239,68,68,0.55)]"
          : "bg-[color:rgba(203,72,226,0.18)] border-[color:rgba(203,72,226,0.55)]";

  return (
    <span
      className={[
        "inline-flex h-6 w-6 items-center justify-center rounded-[10px]",
        "border",
        active ? "bg-[color:rgba(255,255,255,0.06)]" : "bg-transparent",
        active ? fill : "border-[color:rgba(255,255,255,0.40)]",
        "ring-0",
      ].join(" ")}
      aria-hidden="true"
    >
      {active ? (
        <svg
          className="h-3 w-3 text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M4.5 10.5 8 14l7.5-8"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </span>
  );
}

function MiniProgress({
  value,
  tone = "info",
}: {
  value: number;
  tone?: "info" | "success" | "warning" | "danger";
}) {
  const v = Math.max(0, Math.min(100, value));
  const fill =
    tone === "success"
      ? "bg-[color:rgba(34,197,94,0.65)]"
      : tone === "warning"
        ? "bg-[color:rgba(245,158,11,0.70)]"
        : tone === "danger"
          ? "bg-[color:rgba(239,68,68,0.70)]"
          : "bg-gradient-to-r from-[color:var(--kkp-purple)] to-[color:var(--kkp-magenta)]";

  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-[color:rgba(255,255,255,0.10)]">
      <div className={fill} style={{ width: `${v}%`, height: "100%" }} />
    </div>
  );
}

function SandboxDemoPage() {
  const [tab, setTab] = React.useState<TabKey>("overview");

  // Toast demo
  const [variant, setVariant] = React.useState<ToastVariant>("info");
  const [durationMs, setDurationMs] = React.useState<number>(4000);
  const [withAction, setWithAction] = React.useState<boolean>(true);

  // Forms demo
  const [email, setEmail] = React.useState<string>("admin@example.com");
  const [password, setPassword] = React.useState<string>("password");
  const [company, setCompany] = React.useState<string>("PT KamuKirim - Payroll");
  const [amount, setAmount] = React.useState<number>(12500000);
  const [agree, setAgree] = React.useState<boolean>(true);
  const [formMode, setFormMode] = React.useState<"create" | "edit">("create");

  const emailError =
    email.trim().length === 0
      ? "Email is required."
      : !email.includes("@")
        ? "Please enter a valid email."
        : undefined;

  const passwordError =
    password.trim().length === 0
      ? "Password is required."
      : password.trim().length < 6
        ? "Minimum 6 characters."
        : undefined;

  const amountError =
    Number.isFinite(amount) && amount >= 0 ? undefined : "Amount must be ≥ 0.";

  // Data demo
  const [search, setSearch] = React.useState<string>("");
  const rows = React.useMemo(() => {
    const base = [
      {
        id: "CMP-001",
        name: "PT KamuKirim - Payroll",
        status: "Active" as const,
        plan: "Standard",
        employees: 128,
        updatedAt: "2025-01-12 10:21",
      },
      {
        id: "CMP-002",
        name: "CV Nusantara Teknologi",
        status: "Active" as const,
        plan: "Enterprise",
        employees: 512,
        updatedAt: "2025-01-09 08:14",
      },
      {
        id: "CMP-003",
        name: "PT Satu Dua Tiga",
        status: "Suspended" as const,
        plan: "Standard",
        employees: 44,
        updatedAt: "2025-01-03 17:42",
      },
      {
        id: "CMP-004",
        name: "PT Merah Putih",
        status: "Pending" as const,
        plan: "Trial",
        employees: 8,
        updatedAt: "2024-12-29 12:02",
      },
    ];

    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => {
      return (
        r.id.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.plan.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q)
      );
    });
  }, [search]);

  // Navigation demo
  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(true);
  const [pageTitle, setPageTitle] = React.useState<string>("Clients");
  const [env, setEnv] = React.useState<"DEV" | "STAGING" | "PROD">("DEV");

  // Content demo
  const [progress, setProgress] = React.useState<number>(62);
  const [density, setDensity] = React.useState<"comfortable" | "compact">(
    "comfortable",
  );

  return (
    <PageContainer
      title="Sandbox"
      description="Flowbite-style component gallery for KKP-Admin. Use this to validate layout, spacing, tokens, and interactive behaviors before extracting shared `ui/` primitives."
    >
      <div className="grid gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Sections</CardTitle>
            <CardDescription>
              Browse interactive demos. Keep components minimal and extract to
              `ui/` only when they stabilize.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap items-center gap-2">
              <TabButton
                active={tab === "overview"}
                onClick={() => setTab("overview")}
              >
                Overview
              </TabButton>
              <TabButton
                active={tab === "feedback"}
                onClick={() => setTab("feedback")}
              >
                Feedback
              </TabButton>
              <TabButton
                active={tab === "forms"}
                onClick={() => setTab("forms")}
              >
                Forms
              </TabButton>
              <TabButton active={tab === "data"} onClick={() => setTab("data")}>
                Data display
              </TabButton>
              <TabButton
                active={tab === "navigation"}
                onClick={() => setTab("navigation")}
              >
                Navigation
              </TabButton>
              <TabButton
                active={tab === "content"}
                onClick={() => setTab("content")}
              >
                Content
              </TabButton>
            </div>
          </CardBody>
        </Card>

        {tab === "overview" ? (
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Quick Checks</CardTitle>
                <CardDescription>
                  A compact dashboard-style snapshot for validating spacing,
                  typography hierarchy, and surface/border tokens.
                </CardDescription>
              </CardHeader>
              <CardBody>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Stat
                    label="Environment"
                    value={env}
                    note="Environment badge in topbar"
                  />
                  <Stat
                    label="Active company"
                    value={company}
                    note="Used across pages (future)"
                  />
                  <Stat
                    label="Toast duration"
                    value={`${durationMs}ms`}
                    note="0 = sticky"
                  />
                  <Stat
                    label="Table rows"
                    value={`${rows.length}`}
                    note="Filtered with search"
                  />
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                    <Label>Primary actions</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        variant="primary"
                        onClick={() =>
                          toast.success("Primary action clicked .", {
                            title: "Action",
                            durationMs: 2200,
                          })
                        }
                      >
                        Primary
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          toast.info("Secondary action clicked .", {
                            title: "Action",
                            durationMs: 2200,
                          })
                        }
                      >
                        Secondary
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() =>
                          toast.info("Ghost action clicked .", {
                            title: "Action",
                            durationMs: 2200,
                          })
                        }
                      >
                        Ghost
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                    <Label>Status badges</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge tone="success">Active</Badge>
                      <Badge tone="warning">Pending</Badge>
                      <Badge tone="danger">Suspended</Badge>
                      <Badge tone="info">Info</Badge>
                      <Badge tone="neutral">Neutral</Badge>
                    </div>
                  </div>

                  <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                    <Label>Progress</Label>
                    <div className="mt-2 grid gap-2">
                      <MiniProgress value={progress} tone="info" />
                      <div className="flex items-center justify-between gap-3 text-xs text-[color:var(--kkp-text-muted)]">
                        <span>Payroll run status </span>
                        <span className="font-semibold text-white">
                          {progress}%
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setProgress((p) => Math.max(0, p - 8))}
                        >
                          -8
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            setProgress((p) => Math.min(100, p + 8))
                          }
                        >
                          +8
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setProgress(62)}
                        >
                          Reset
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Playground shortcuts</CardTitle>
                <CardDescription>
                  Quick buttons for common feedback states (used across pages).
                </CardDescription>
              </CardHeader>
              <CardBody>
                <div className="grid gap-2 sm:grid-cols-3">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      toast.success("Saved settings .", {
                        title: "Success",
                        durationMs: 2500,
                      })
                    }
                  >
                    Simulate Save
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() =>
                      toast.warning("There are pending approvals.", {
                        title: "Heads up",
                        durationMs: 3500,
                      })
                    }
                  >
                    Simulate Warning
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() =>
                      toast.danger("Something went wrong .", {
                        title: "Error",
                        durationMs: 0,
                      })
                    }
                  >
                    Simulate Error (sticky)
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : null}

        {tab === "feedback" ? (
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Toast Demo</CardTitle>
                <CardDescription>
                  Flowbite-inspired toast controls backed by our in-app toast
                  container. Uses KamuKirim brand tokens.
                </CardDescription>
              </CardHeader>

              <CardBody>
                <div className="grid gap-3 lg:grid-cols-3">
                  <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                    <Label>Variant</Label>
                    <div className="mt-2 inline-flex flex-wrap items-center gap-1">
                      {(["info", "success", "warning", "danger"] as const).map(
                        (v) => {
                          const active = variant === v;
                          return (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setVariant(v)}
                              className={[
                                "inline-flex items-center gap-2 rounded-[999px] px-3 py-1.5 text-xs font-bold",
                                "transition",
                                active
                                  ? "bg-[color:rgba(255,255,255,0.06)] text-white"
                                  : "bg-transparent text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.04)]",
                              ].join(" ")}
                              aria-pressed={active}
                            >
                              <Swatch variant={v} active={active} />
                              {variantLabel(v)}
                            </button>
                          );
                        },
                      )}
                    </div>

                    <div className="mt-3 text-xs text-[color:var(--kkp-text-muted)]">
                      Tip: set duration to{" "}
                      <span className="font-semibold text-white">0</span> to
                      keep the toast until dismissed.
                    </div>
                  </div>

                  <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                    <Label>Options</Label>
                    <div className="mt-2 grid gap-2">
                      <label className="flex items-center justify-between gap-3 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                        <span className="text-xs text-[color:rgba(255,255,255,0.88)]">
                          Duration (ms)
                        </span>
                        <input
                          className="kkp-input w-[140px] text-right"
                          type="number"
                          min={0}
                          step={500}
                          value={durationMs}
                          onChange={(e) =>
                            setDurationMs(Number(e.target.value || 0))
                          }
                          aria-label="Toast duration in milliseconds"
                        />
                      </label>

                      <label className="flex items-center justify-between gap-3 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                        <span className="text-xs text-[color:rgba(255,255,255,0.88)]">
                          Include action button
                        </span>
                        <input
                          type="checkbox"
                          checked={withAction}
                          onChange={(e) => setWithAction(e.target.checked)}
                          className="h-4 w-4 accent-[color:var(--kkp-magenta)]"
                          aria-label="Include action button"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                    <Label>Actions</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          toast.info("Hello from KKP-Admin", {
                            title: "Toast demo",
                            durationMs: 2500,
                          });
                        }}
                      >
                        Quick Info
                      </Button>

                      <Button
                        variant="primary"
                        onClick={() => {
                          toast.show({
                            variant,
                            title: `Demo: ${variantLabel(variant)}`,
                            message:
                              "This is a Flowbite-style toast rendered with brand tokens.",
                            durationMs,
                            action: withAction
                              ? {
                                label: "Undo",
                                onClick: () => {
                                  toast.success("Undo clicked .", {
                                    title: "Action",
                                  });
                                },
                              }
                              : undefined,
                          });
                        }}
                      >
                        Trigger Toast
                      </Button>
                    </div>

                    <Divider label="Reusable component" />

                    <div className="mt-3 flex items-center justify-between gap-2 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                      <span className="text-xs text-[color:var(--kkp-text-muted)]">
                        Trigger via `ToastDemo`:
                      </span>
                      <ToastDemo
                        variant={variant}
                        title={`Demo: ${variantLabel(variant)}`}
                        message="Reusable toast demo using shared component."
                        label="ToastDemo"
                      />
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : null}

        {tab === "forms" ? (
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Form Patterns</CardTitle>
                <CardDescription>
                  Input groups, validation hints, and action bars. This is
                  UI-only (no API).
                </CardDescription>
              </CardHeader>
              <CardBody>
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="grid gap-3 rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-white">
                          {formMode === "create"
                            ? "Create company"
                            : "Edit company"}
                        </div>
                        <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
                          Shows inline validation without extra dependencies.
                        </div>
                      </div>
                      <div className="shrink-0">
                        <button
                          type="button"
                          className="rounded-full border border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.03)] px-3 py-1.5 text-xs font-bold text-white transition hover:bg-[color:rgba(255,255,255,0.06)]"
                          onClick={() =>
                            setFormMode((m) =>
                              m === "create" ? "edit" : "create",
                            )
                          }
                        >
                          Toggle mode
                        </button>
                      </div>
                    </div>

                    <Divider />

                    <KkpInput
                      id="company"
                      label="Company name"
                      hint="Example: PT KamuKirim - Payroll"
                      right={<span className="font-semibold">Required</span>}
                    >
                      <input
                        id="company"
                        className="kkp-input w-full"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Company"
                      />
                    </KkpInput>

                    <KkpInput
                      id="email"
                      label="Admin email"
                      hint="Used for notifications and login ."
                      error={emailError}
                      right={<span className="font-semibold">Required</span>}
                    >
                      <input
                        id="email"
                        className="kkp-input w-full"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@company.com"
                        inputMode="email"
                        autoComplete="email"
                      />
                    </KkpInput>

                    <KkpInput
                      id="password"
                      label="Temporary password"
                      hint="Use a secure password (UI-only placeholder)."
                      error={passwordError}
                      right={<span className="font-semibold">Required</span>}
                    >
                      <input
                        id="password"
                        className="kkp-input w-full"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        type="password"
                        autoComplete="current-password"
                      />
                    </KkpInput>

                    <KkpInput
                      id="amount"
                      label="Monthly payroll estimate"
                      hint="Validates non-negative values."
                      error={amountError}
                      right={<span className="font-semibold">Optional</span>}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[color:rgba(255,255,255,0.72)]">
                          Rp
                        </span>
                        <input
                          id="amount"
                          className="kkp-input w-full text-right"
                          value={String(amount)}
                          onChange={(e) =>
                            setAmount(Number(e.target.value || 0))
                          }
                          type="number"
                          min={0}
                          step={50000}
                        />
                      </div>
                    </KkpInput>

                    <label className="flex items-start justify-between gap-3 rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                      <span className="text-xs text-[color:rgba(255,255,255,0.88)]">
                        I confirm data is correct
                        <div className="mt-0.5 text-[11px] text-[color:var(--kkp-text-muted)]">
                          Helps test checkbox alignment and density.
                        </div>
                      </span>
                      <input
                        type="checkbox"
                        checked={agree}
                        onChange={(e) => setAgree(e.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-[color:var(--kkp-magenta)]"
                        aria-label="Confirm"
                      />
                    </label>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-xs text-[color:var(--kkp-text-muted)]">
                        Validation is computed locally (no form library here).
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setCompany("PT KamuKirim - Payroll");
                            setEmail("admin@example.com");
                            setPassword("password");
                            setAmount(12500000);
                            setAgree(true);
                            toast.info("Reset form .", {
                              title: "Form",
                              durationMs: 1800,
                            });
                          }}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="primary"
                          disabled={
                            Boolean(emailError || passwordError) || !agree
                          }
                          onClick={() => {
                            toast.success("Submitted .", {
                              title:
                                formMode === "create"
                                  ? "Company created"
                                  : "Company updated",
                              durationMs: 2600,
                            });
                          }}
                        >
                          {formMode === "create" ? "Create" : "Save changes"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3">
                    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                      <Label>Density</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          className={[
                            "rounded-full border px-3 py-1.5 text-xs font-bold transition",
                            density === "comfortable"
                              ? "border-[color:rgba(255,255,255,0.18)] bg-[color:rgba(255,255,255,0.06)] text-white"
                              : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
                          ].join(" ")}
                          onClick={() => setDensity("comfortable")}
                        >
                          Comfortable
                        </button>
                        <button
                          type="button"
                          className={[
                            "rounded-full border px-3 py-1.5 text-xs font-bold transition",
                            density === "compact"
                              ? "border-[color:rgba(255,255,255,0.18)] bg-[color:rgba(255,255,255,0.06)] text-white"
                              : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
                          ].join(" ")}
                          onClick={() => setDensity("compact")}
                        >
                          Compact
                        </button>
                      </div>
                      <div className="mt-2 text-xs text-[color:var(--kkp-text-muted)]">
                        Use density selection to validate layout at different
                        spacing presets (inform future tokens).
                      </div>
                    </div>

                    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                      <Label>Keyboard + focus</Label>
                      <div className="mt-2 text-xs text-[color:var(--kkp-text-muted)]">
                        Ensure focus ring remains visible on dark surfaces using
                        magenta ring tokens.
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button variant="secondary">Focusable</Button>
                        <Button variant="ghost">Focusable</Button>
                        <Button variant="primary">Focusable</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : null}

        {tab === "data" ? (
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Data Table</CardTitle>
                <CardDescription>
                  Table styling and empty states. Avoid full-bleed layouts
                  unless needed.
                </CardDescription>
              </CardHeader>
              <CardBody>
                <div className="grid gap-3">
                  <div className="grid gap-2 sm:grid-cols-3">
                    <KkpInput
                      id="search"
                      label="Search companies"
                      hint="Search by id, name, plan, status."
                      right={
                        <span className="font-semibold">
                          {rows.length} result(s)
                        </span>
                      }
                    >
                      <input
                        id="search"
                        className="kkp-input w-full"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search…"
                      />
                    </KkpInput>

                    <div className="grid gap-1">
                      <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.92)]">
                        Quick filters
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setSearch("Active")}
                        >
                          Active
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setSearch("Enterprise")}
                        >
                          Enterprise
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSearch("")}
                        >
                          Clear
                        </Button>
                      </div>
                      <div className="text-xs text-[color:var(--kkp-text-muted)]">
                        Buttons simulate filter chips.
                      </div>
                    </div>

                    <div className="grid gap-1">
                      <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.92)]">
                        Row actions
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() =>
                            toast.success("Add Company .", {
                              title: "Dialog",
                              durationMs: 2200,
                            })
                          }
                        >
                          Add Company
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            toast.info("Export CSV .", {
                              title: "Export",
                              durationMs: 2200,
                            })
                          }
                        >
                          Export CSV
                        </Button>
                      </div>
                      <div className="text-xs text-[color:var(--kkp-text-muted)]">
                        Replace with real dialogs later.
                      </div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)]">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="bg-[color:rgba(255,255,255,0.04)]">
                          <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.70)]">
                            <th className="px-4 py-3">Company</th>
                            <th className="px-4 py-3">Plan</th>
                            <th className="px-4 py-3">Employees</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Updated</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="px-4 py-6 text-center text-sm text-[color:var(--kkp-text-muted)]"
                              >
                                No results. Try a different search query.
                              </td>
                            </tr>
                          ) : (
                            rows.map((r) => (
                              <tr
                                key={r.id}
                                className="border-t border-[color:rgba(255,255,255,0.08)]"
                              >
                                <td className="px-4 py-3">
                                  <div className="text-sm font-bold text-white">
                                    {r.name}
                                  </div>
                                  <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
                                    {r.id}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-sm text-[color:rgba(255,255,255,0.86)]">
                                    {r.plan}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-sm text-[color:rgba(255,255,255,0.86)]">
                                    {r.employees}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  {r.status === "Active" ? (
                                    <Badge tone="success">Active</Badge>
                                  ) : r.status === "Pending" ? (
                                    <Badge tone="warning">Pending</Badge>
                                  ) : (
                                    <Badge tone="danger">Suspended</Badge>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-xs text-[color:var(--kkp-text-muted)]">
                                    {r.updatedAt}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={() =>
                                        toast.info("Open detail .", {
                                          title: r.name,
                                          durationMs: 2200,
                                        })
                                      }
                                    >
                                      View
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        toast.warning("Edit disabled .", {
                                          title: "Edit",
                                          durationMs: 2200,
                                        })
                                      }
                                    >
                                      Edit
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : null}

        {tab === "navigation" ? (
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Navigation Shell</CardTitle>
                <CardDescription>
                  Preview the sidebar/topbar feel without touching the real app
                  layout. Useful for spacing + token tuning.
                </CardDescription>
              </CardHeader>
              <CardBody>
                <div className="grid gap-3 lg:grid-cols-3">
                  <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                    <Label>Controls</Label>
                    <div className="mt-2 grid gap-2">
                      <label className="flex items-center justify-between gap-3 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                        <span className="text-xs text-[color:rgba(255,255,255,0.88)]">
                          Sidebar open
                        </span>
                        <input
                          type="checkbox"
                          checked={sidebarOpen}
                          onChange={(e) => setSidebarOpen(e.target.checked)}
                          className="h-4 w-4 accent-[color:var(--kkp-magenta)]"
                        />
                      </label>

                      <label className="grid gap-2 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                        <span className="text-xs text-[color:rgba(255,255,255,0.88)]">
                          Page title
                        </span>
                        <input
                          className="kkp-input w-full"
                          value={pageTitle}
                          onChange={(e) => setPageTitle(e.target.value)}
                          placeholder="Title"
                        />
                      </label>

                      <div className="grid gap-1">
                        <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
                          Environment badge
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setEnv("DEV")}
                          >
                            DEV
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setEnv("STAGING")}
                          >
                            STAGING
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setEnv("PROD")}
                          >
                            PROD
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <div className="overflow-hidden rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:rgba(0,0,0,0.12)]">
                      <div className="flex min-h-[260px]">
                        {sidebarOpen ? (
                          <div className="w-[260px] border-r border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(0,0,0,0.18)] p-3">
                            <div className="text-sm font-bold text-white">
                              KamuKirim - Payroll
                            </div>
                            <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
                              Sidebar
                            </div>

                            <div className="mt-3 grid gap-1">
                              {["Clients", "Logs", "Sandbox"].map((item) => {
                                const active = item === "Sandbox";
                                return (
                                  <div
                                    key={item}
                                    className={[
                                      "flex items-center justify-between rounded-full px-3 py-2 text-sm font-semibold",
                                      active
                                        ? "bg-[color:rgba(255,255,255,0.06)] text-white"
                                        : "text-[color:rgba(255,255,255,0.78)] hover:bg-[color:rgba(255,255,255,0.04)]",
                                    ].join(" ")}
                                  >
                                    <span>{item}</span>
                                    {active ? (
                                      <span className="h-2 w-2 rounded-full bg-[color:var(--kkp-magenta)]" />
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>

                            <div className="mt-4 text-xs text-[color:var(--kkp-text-muted)]">
                              Signed in as
                              <div className="mt-1 text-sm font-bold text-white">
                                Immer Ext-auditor
                              </div>
                            </div>

                            <div className="mt-3">
                              <Button
                                variant="secondary"
                                fullWidth
                                onClick={() =>
                                  toast.info("Logout clicked .", {
                                    title: "Session",
                                    durationMs: 2000,
                                  })
                                }
                              >
                                Logout
                              </Button>
                            </div>
                          </div>
                        ) : null}

                        <div className="min-w-0 flex-1 p-3">
                          <div className="flex items-center justify-between gap-3 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-bold text-white">
                                {pageTitle || "Untitled"}
                              </div>
                              <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
                                Topbar
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge tone="neutral">{env}</Badge>
                              <Badge tone="info">v0.0.1</Badge>
                            </div>
                          </div>

                          <div className="mt-3 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] p-3">
                            <div className="text-sm font-bold text-white">
                              Content area
                            </div>
                            <div className="mt-1 text-xs text-[color:var(--kkp-text-muted)]">
                              Validate spacing, container widths, and contrast
                              here.
                            </div>

                            <div className="mt-3 grid gap-2 sm:grid-cols-3">
                              <Stat label="Metric A" value="1,248" note="KPI" />
                              <Stat label="Metric B" value="98%" note="KPI" />
                              <Stat label="Metric C" value="12" note="KPI" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 text-xs text-[color:var(--kkp-text-muted)]">
                      Note: This is a sandbox-only preview. The real shell lives
                      in the Dashboard layout.
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : null}

        {tab === "content" ? (
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Content Blocks</CardTitle>
                <CardDescription>
                  Cards, lists, and read-only details. Designed to be calm and
                  readable with on-brand tokens.
                </CardDescription>
              </CardHeader>
              <CardBody>
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="grid gap-3 lg:col-span-2">
                    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-white">
                            Payroll Run
                          </div>
                          <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
                            Read-only detail block with subtle dividers.
                          </div>
                        </div>
                        <Badge tone="warning">Review</Badge>
                      </div>

                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <div className="rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                          <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
                            Period
                          </div>
                          <div className="mt-0.5 text-sm font-bold text-white">
                            Jan 2025
                          </div>
                        </div>
                        <div className="rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                          <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
                            Company
                          </div>
                          <div className="mt-0.5 truncate text-sm font-bold text-white">
                            {company}
                          </div>
                        </div>
                        <div className="rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                          <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
                            Total
                          </div>
                          <div className="mt-0.5 text-sm font-bold text-white">
                            Rp {amount.toLocaleString("id-ID")}
                          </div>
                        </div>
                        <div className="rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2">
                          <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
                            Progress
                          </div>
                          <div className="mt-2">
                            <MiniProgress value={progress} tone="info" />
                          </div>
                        </div>
                      </div>

                      <Divider />

                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs text-[color:var(--kkp-text-muted)]">
                          Actions
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                              toast.info("Open review checklist .", {
                                title: "Checklist",
                                durationMs: 2400,
                              })
                            }
                          >
                            Checklist
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() =>
                              toast.success("Approved .", {
                                title: "Payroll run",
                                durationMs: 2400,
                              })
                            }
                          >
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                      <div className="text-sm font-bold text-white">
                        Activity
                      </div>
                      <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
                        List styling for audit-log-like items.
                      </div>

                      <div className="mt-3 grid gap-2">
                        {[
                          {
                            who: "Ops",
                            what: "Uploaded template file",
                            when: "10:04",
                            tone: "info" as const,
                          },
                          {
                            who: "Finance",
                            what: "Marked run for review",
                            when: "10:18",
                            tone: "warning" as const,
                          },
                          {
                            who: "Auditor",
                            what: "Approved changes",
                            when: "11:02",
                            tone: "success" as const,
                          },
                        ].map((e, idx) => (
                          <div
                            key={idx}
                            className="flex items-start justify-between gap-3 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.10)] bg-[color:rgba(255,255,255,0.02)] px-3 py-2"
                          >
                            <div className="min-w-0">
                              <div className="text-xs font-bold text-white">
                                {e.who} •{" "}
                                <span className="font-semibold text-[color:rgba(255,255,255,0.78)]">
                                  {e.what}
                                </span>
                              </div>
                              <div className="mt-0.5 text-[11px] text-[color:var(--kkp-text-muted)]">
                                Audit event
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge tone={e.tone}>
                                {e.tone.toUpperCase()}
                              </Badge>
                              <span className="text-[11px] text-[color:var(--kkp-text-muted)]">
                                {e.when}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3">
                    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                      <Label>Quick actions</Label>
                      <div className="mt-2 grid gap-2">
                        <Button
                          variant="secondary"
                          onClick={() =>
                            toast.info("Open upload .", {
                              title: "Upload",
                              durationMs: 2200,
                            })
                          }
                          fullWidth
                        >
                          Upload file
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            toast.info("Open export .", {
                              title: "Export",
                              durationMs: 2200,
                            })
                          }
                          fullWidth
                        >
                          Export summary
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() =>
                            toast.danger("Archive disabled .", {
                              title: "Archive",
                              durationMs: 0,
                            })
                          }
                          fullWidth
                        >
                          Archive run
                        </Button>
                      </div>
                    </div>

                    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] p-3">
                      <Label>Notes</Label>
                      <div className="mt-2 text-xs text-[color:var(--kkp-text-muted)]">
                        Use this page to trial small UI pieces before moving
                        them into shared primitives.
                      </div>
                      <div className="mt-2 text-xs text-[color:var(--kkp-text-muted)]">
                        Keep changes incremental and avoid introducing new
                        dependencies unless required.
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : null}
      </div>
    </PageContainer>
  );
}
